import type { SVGProps } from 'react';

/**
 * Navigation icons, supplied by the client as a set.
 *
 * The originals ship with their greys baked in (`#93A1B8` / `#8F98B2`). Those
 * are swapped for `currentColor` so one icon serves every state — muted, hover
 * and the active tint — instead of needing a second copy per colour.
 *
 * Geometry is otherwise untouched, including the mix of stroked and filled
 * shapes, so they still look like the set that was drawn.
 */

export type NavIconProps = SVGProps<SVGSVGElement>;

/** Shared frame: the set is drawn on a 24×22 canvas. */
const base = (props: NavIconProps) => ({
  width: 24,
  height: 22,
  viewBox: '0 0 24 22',
  fill: 'none',
  xmlns: 'http://www.w3.org/2000/svg',
  'aria-hidden': true,
  focusable: false,
  ...props,
});

export function HomeIcon(props: NavIconProps) {
  return (
    <svg {...base(props)}>
      <path
        d="M13.75 16.25V11.5833C13.75 11.4286 13.6885 11.2803 13.5791 11.1709C13.4697 11.0615 13.3214 11 13.1667 11H10.8333C10.6786 11 10.5303 11.0615 10.4209 11.1709C10.3115 11.2803 10.25 11.4286 10.25 11.5833V16.25"
        stroke="currentColor"
        strokeWidth="1.05"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M6.75 9.83296C6.74996 9.66325 6.78694 9.49558 6.85838 9.34163C6.92981 9.18768 7.03397 9.05118 7.16358 8.94163L11.2469 5.44163C11.4575 5.26366 11.7243 5.16602 12 5.16602C12.2757 5.16602 12.5425 5.26366 12.7531 5.44163L16.8364 8.94163C16.966 9.05118 17.0702 9.18768 17.1416 9.34163C17.2131 9.49558 17.25 9.66325 17.25 9.83296V15.083C17.25 15.3924 17.1271 15.6891 16.9083 15.9079C16.6895 16.1267 16.3928 16.2496 16.0833 16.2496H7.91667C7.60725 16.2496 7.3105 16.1267 7.09171 15.9079C6.87292 15.6891 6.75 15.3924 6.75 15.083V9.83296Z"
        stroke="currentColor"
        strokeWidth="1.05"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export function TeamIcon(props: NavIconProps) {
  return (
    <svg {...base(props)}>
      <path
        d="M6.964 16.5V15.226H7.804V14.274H8.392L8.924 13.322V9.066L8.392 8.114H7.804V6.434H9.54V6.84H10.38V6.49H12.634V6.84H13.474V6.434H15.21V8.114H14.608L14.09 9.066V13.322L14.608 14.274H15.21V15.226H16.05V16.5H6.964ZM8.224 7.792H14.79V7.498H8.224V7.792ZM9.064 8.604H13.95L14.118 8.31H8.896L9.064 8.604ZM8.854 14.134H14.16L13.992 13.84H9.022L8.854 14.134ZM8.224 15.086H14.79V14.792H8.224V15.086ZM7.384 16.038H15.63V15.744H7.384V16.038Z"
        fill="currentColor"
      />
    </svg>
  );
}

export function TransfersIcon(props: NavIconProps) {
  return (
    <svg {...base(props)}>
      <path
        d="M14.392 12.676H13.708C14.092 11.824 14.488 11.176 14.86 10.756H6.112V9.904H14.86C14.476 9.484 14.092 8.836 13.708 7.996H14.392C15.232 8.98 16.12 9.7 17.032 10.168V10.504C16.12 10.972 15.232 11.692 14.392 12.676ZM8.752 17.356C7.912 16.372 7.024 15.652 6.112 15.184V14.848C7.024 14.38 7.912 13.66 8.752 12.676H9.436C9.052 13.516 8.668 14.164 8.284 14.584H17.032V15.436H8.284C8.656 15.856 9.052 16.504 9.436 17.356H8.752Z"
        fill="currentColor"
      />
    </svg>
  );
}

export function FixturesIcon(props: NavIconProps) {
  return (
    <svg {...base(props)}>
      <path
        d="M6.43296 17.5V6.56058H17.3724V17.5H6.43296ZM7.10981 16.8231H11.5734V7.23744H7.10981V16.8231ZM12.2319 16.8231H16.6955V7.23744H12.2319V16.8231Z"
        fill="currentColor"
      />
    </svg>
  );
}

export function LeaguesIcon(props: NavIconProps) {
  return (
    <svg {...base(props)}>
      <path
        d="M9.58192 10.319C10.1818 9.99826 11.0353 9.7873 12.0006 9.7873C12.966 9.7873 13.8195 9.99826 14.4194 10.319C14.6325 10.4329 14.8975 10.3525 15.0115 10.1394C15.1254 9.92632 15.045 9.66123 14.8319 9.54731C14.0825 9.14665 13.0818 8.9123 12.0006 8.9123C10.9195 8.9123 9.91879 9.14665 9.16938 9.54731C8.95629 9.66123 8.87591 9.92632 8.98983 10.1394C9.10375 10.3525 9.36884 10.4329 9.58192 10.319Z"
        fill="currentColor"
      />
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M8.35482 4.48783C8.35482 4.37793 8.47176 4.30753 8.56888 4.35894L10.576 5.42155C10.6823 5.4778 10.6787 5.63121 10.5699 5.68239L9.22982 6.31302V7.45969C10.0551 7.26881 11.0003 7.1623 11.9984 7.1623C12.7703 7.1623 13.5105 7.226 14.1882 7.34317V4.48783C14.1882 4.37793 14.3051 4.30753 14.4022 4.35894L16.4094 5.42155C16.5156 5.4778 16.512 5.63121 16.4032 5.68239L15.0632 6.31302V7.53318C15.3916 7.62039 15.6982 7.72153 15.9782 7.83505C16.4973 8.0455 16.9465 8.3071 17.2729 8.61998C17.6005 8.93396 17.8317 9.32842 17.8317 9.7873L17.8317 9.79789L17.8317 14.1331C17.8317 14.4551 17.7145 14.8085 17.4226 15.0514C16.806 15.5644 15.947 15.9233 15.0059 16.1546C14.0587 16.3875 12.9962 16.4991 11.9439 16.4956C10.8916 16.492 9.83685 16.3734 8.90528 16.1381C7.98077 15.9046 7.14275 15.5475 6.55562 15.0433C6.27363 14.8011 6.16504 14.4555 6.16504 14.1451L6.16504 9.7873C6.16504 9.32842 6.39626 8.93396 6.72384 8.61998C7.05026 8.3071 7.49946 8.0455 8.01857 7.83505C8.12678 7.79119 8.23895 7.74917 8.35482 7.70909V4.48783ZM16.9567 14.1331L16.9567 11.2157C16.678 11.416 16.3456 11.5906 15.9782 11.7395C14.9377 12.1614 13.5304 12.4123 11.9984 12.4123C10.4663 12.4123 9.05904 12.1614 8.01857 11.7395C7.65112 11.5906 7.31869 11.416 7.04004 11.2157L7.04004 14.1451C7.04004 14.2649 7.08135 14.3414 7.12571 14.3795C7.56869 14.76 8.26049 15.0728 9.11954 15.2897C9.97153 15.5049 10.9541 15.6172 11.9468 15.6206C12.9395 15.6239 13.9297 15.5182 14.797 15.3049C15.6704 15.0902 16.3876 14.7742 16.863 14.3787C16.9128 14.3373 16.9567 14.2557 16.9567 14.1331ZM16.6674 9.25167C16.886 9.46111 16.9567 9.64211 16.9567 9.7873L16.9567 9.79336C16.9547 9.93751 16.8829 10.1164 16.6674 10.3229C16.4478 10.5335 16.1068 10.7432 15.6494 10.9286C14.737 11.2986 13.4464 11.5373 11.9984 11.5373C10.5504 11.5373 9.25975 11.2986 8.34731 10.9286C7.88997 10.7432 7.54897 10.5335 7.3293 10.3229C7.11079 10.1135 7.04004 9.93248 7.04004 9.7873C7.04004 9.64211 7.11079 9.46111 7.3293 9.25167C7.54897 9.04113 7.88997 8.83136 8.34731 8.64595C9.25975 8.27604 10.5504 8.0373 11.9984 8.0373C13.4464 8.0373 14.737 8.27604 15.6494 8.64595C16.1068 8.83136 16.4478 9.04113 16.6674 9.25167Z"
        fill="currentColor"
      />
    </svg>
  );
}

export function LeaderboardIcon(props: NavIconProps) {
  return (
    <svg {...base(props)}>
      <path
        d="M7.04914 8.42171V6.96114H16.7177V8.42171H7.04914ZM7.04914 12.0011V10.5406H16.7177V12.0011H7.04914ZM7.04914 15.5806V14.12H16.7177V15.5806H7.04914Z"
        fill="currentColor"
      />
    </svg>
  );
}

export function ProfileIcon(props: NavIconProps) {
  return (
    <svg {...base(props)}>
      <path
        d="M17.0241 16.0246V14.9083C17.0241 14.3162 16.7216 13.7484 16.1833 13.3297C15.645 12.911 14.9149 12.6758 14.1536 12.6758H9.84797C9.08669 12.6758 8.35658 12.911 7.81827 13.3297C7.27996 13.7484 6.97754 14.3162 6.97754 14.9083V16.0246"
        stroke="currentColor"
        strokeWidth="1.00465"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M12.0001 10.4417C13.2331 10.4417 14.2327 9.44213 14.2327 8.20912C14.2327 6.97611 13.2331 5.97656 12.0001 5.97656C10.7671 5.97656 9.76758 6.97611 9.76758 8.20912C9.76758 9.44213 10.7671 10.4417 12.0001 10.4417Z"
        stroke="currentColor"
        strokeWidth="1.00465"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/**
 * Contests and Points were not in the supplied set.
 *
 * Drawn to match it rather than pulled from an icon library: same 24×22 frame,
 * same 1.05 stroke, same round joins, so the nav reads as one set.
 */
export function ContestsIcon(props: NavIconProps) {
  return (
    <svg {...base(props)}>
      <path
        d="M9.25 5.75H14.75V9.5C14.75 11.019 13.519 12.25 12 12.25C10.481 12.25 9.25 11.019 9.25 9.5V5.75Z"
        stroke="currentColor"
        strokeWidth="1.05"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M9.25 6.75H7.75C7.75 8.4 8.4 9.25 9.25 9.25M14.75 6.75H16.25C16.25 8.4 15.6 9.25 14.75 9.25"
        stroke="currentColor"
        strokeWidth="1.05"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M12 12.25V14.5M10.75 16.25C10.75 15.2 11.3 14.5 12 14.5C12.7 14.5 13.25 15.2 13.25 16.25M9.75 16.25H14.25"
        stroke="currentColor"
        strokeWidth="1.05"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export function PointsIcon(props: NavIconProps) {
  return (
    <svg {...base(props)}>
      <path
        d="M12 5.75L13.7451 9.29639L17.6574 9.86475L14.8287 12.6255L15.4902 16.5225L12 14.6816L8.50977 16.5225L9.17725 12.6255L6.34265 9.86475L10.2549 9.29639L12 5.75Z"
        stroke="currentColor"
        strokeWidth="1.05"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
